import { API_BASE_URL, GLOBAL_EVENTS_BASE_URL } from '../config/api';

interface ApiError {
    message: string;
    status: number;
}

export interface GlobalEvent {
    command: 'SLACK_MUTED' | 'SLACK_UNMUTED';
}

class ApiService {
    private async handleResponse<T>(response: Response): Promise<T> {
        if (!response.ok) {
            const error: ApiError = {
                message: response.statusText,
                status: response.status
            };
            throw error;
        }
        return response.json();
    }

    async get<T>(endpoint: string): Promise<T> {
        const response = await fetch(`${API_BASE_URL}${endpoint}`, {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json'
            }
        });
        return this.handleResponse<T>(response);
    }

    async post<T>(endpoint: string, data: any): Promise<T> {
        const response = await fetch(`${API_BASE_URL}${endpoint}`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json'
            },
            body: JSON.stringify(data)
        });
        return this.handleResponse<T>(response);
    }

    // For endpoints that answer with an empty body.
    async postNoContent(endpoint: string, data?: any): Promise<void> {
        const response = await fetch(`${API_BASE_URL}${endpoint}`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json'
            },
            body: data ? JSON.stringify(data) : undefined
        });
        if (!response.ok) {
            const error: ApiError = {
                message: response.statusText,
                status: response.status
            };
            throw error;
        }
    }

    async put<T>(endpoint: string, data: any): Promise<T> {
        const response = await fetch(`${API_BASE_URL}${endpoint}`, {
            method: 'PUT',
            headers: {
                'Content-Type': 'application/json'
            },
            body: JSON.stringify(data)
        });
        return this.handleResponse<T>(response);
    }
    
    async patch<T>(endpoint: string, data?: any): Promise<T> {
        const response = await fetch(`${API_BASE_URL}${endpoint}`, {
            method: 'PATCH',
            headers: {
                'Content-Type': 'application/json'
            },
            body: data ? JSON.stringify(data) : undefined
        });
        return this.handleResponse<T>(response);
    }

    async sendGlobalEvent(event: GlobalEvent): Promise<void> {
        const response = await fetch(GLOBAL_EVENTS_BASE_URL, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json'
            },
            body: JSON.stringify(event)
        });
        
        if (!response.ok) {
            const error: ApiError = {
                message: response.statusText,
                status: response.status
            };
            throw error;
        }
    }

    // Recovers health-suspended markets on upcoming events (by_health_state false -> true).
    // Returns the number of market availability states that were restored.
    async recoverMarketsHealth(): Promise<{ updated: number }> {
        return this.post<{ updated: number }>('/health/markets/recover', {});
    }
}

export const apiService = new ApiService();
